import { useState, useRef, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { TextField, TextFieldProps, InputAdornment, Box } from '@mui/material';
import { COUNTRY_CODES } from 'src/utils/phoneValidation';

const DEFAULT_PREFIX = '+48';

/**
 * Extract the country code prefix from a full phone number like "+48123456789".
 * Tries longest match first (3-digit codes, then 2, then 1).
 */
function extractParts(phone: string): { prefix: string; number: string } {
  if (!phone || !phone.startsWith('+')) {
    return { prefix: DEFAULT_PREFIX, number: phone || '' };
  }

  const withoutPlus = phone.slice(1);

  for (const len of [3, 2, 1]) {
    const candidate = withoutPlus.slice(0, len);
    if (COUNTRY_CODES.includes(candidate as (typeof COUNTRY_CODES)[number])) {
      return { prefix: `+${candidate}`, number: withoutPlus.slice(len) };
    }
  }

  return { prefix: DEFAULT_PREFIX, number: withoutPlus };
}

type RHFPhoneFieldProps = Omit<TextFieldProps, 'name'> & {
  name: string;
};

export default function RHFPhoneField({
  name,
  helperText,
  InputProps,
  disabled,
  ...other
}: RHFPhoneFieldProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <PhoneFieldInner
          field={field}
          error={error}
          helperText={helperText}
          InputProps={InputProps}
          disabled={disabled}
          other={other}
        />
      )}
    />
  );
}

function PhoneFieldInner({
  field,
  error,
  helperText,
  InputProps,
  disabled,
  other,
}: {
  field: { value: unknown; onChange: (val: string) => void; onBlur: () => void };
  error: { message?: string } | undefined;
  helperText: React.ReactNode;
  InputProps: TextFieldProps['InputProps'];
  disabled: boolean | undefined;
  other: Record<string, unknown>;
}) {
  const fullValue = (field.value as string) || '';
  const initialParts = useRef(extractParts(fullValue));

  const [prefix, setPrefix] = useState(initialParts.current.prefix);
  const [number, setNumber] = useState(initialParts.current.number);

  // Sync from external form value changes (e.g. reset, prefill)
  const lastEmitted = useRef(fullValue);
  useEffect(() => {
    if (fullValue !== lastEmitted.current) {
      const parts = extractParts(fullValue);
      setPrefix(parts.prefix);
      setNumber(parts.number);
      lastEmitted.current = fullValue;
    }
  }, [fullValue]);

  const emitChange = (newPrefix: string, newNumber: string) => {
    const combined = newNumber ? `${newPrefix}${newNumber}` : '';
    lastEmitted.current = combined;
    field.onChange(combined);
  };

  const handlePrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (!val.startsWith('+')) val = `+${val}`;
    val = '+' + val.slice(1).replace(/[^\d]/g, '');
    setPrefix(val);
    emitChange(val, number);
  };

  const handlePrefixBlur = () => {
    // Reset to default if user left prefix empty (just "+")
    if (prefix === '+') {
      setPrefix(DEFAULT_PREFIX);
      emitChange(DEFAULT_PREFIX, number);
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^\d]/g, '');
    setNumber(digits);
    emitChange(prefix, digits);
  };

  return (
    <TextField
      {...other}
      fullWidth
      disabled={disabled}
      value={number}
      onChange={handleNumberChange}
      onBlur={field.onBlur}
      error={!!error}
      helperText={error ? error.message : helperText}
      inputProps={{ 'data-testid': 'phone-number-digits' }}
      InputProps={{
        ...InputProps,
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: 0 }}>
            {InputProps?.startAdornment}
            <Box
              component="input"
              data-testid="phone-prefix"
              value={prefix}
              onChange={handlePrefixChange}
              onBlur={handlePrefixBlur}
              disabled={disabled}
              sx={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                font: 'inherit',
                color: disabled ? 'text.disabled' : 'inherit',
                WebkitTextFillColor: disabled ? (theme) => theme.palette.text.disabled : undefined,
                width: `${Math.max(prefix.length + 1, 3)}ch`,
                p: 0,
                mr: 0.5,
              }}
            />
          </InputAdornment>
        ),
      }}
    />
  );
}
